import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Button,
	Container,
	Paper,
	Stack,
	Typography,
	TableContainer,
	Table,
	TableRow,
	TableHead,
	TableCell,
	TableBody,
	TextField,
	Grid,
	FormControl,
	InputLabel,
	Select,
	MenuItem
} from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronCircleLeft, faSave } from '@fortawesome/free-solid-svg-icons';

import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import { DataContext } from '../../../../context/DataContext';
import { getRuta, simpleMessage } from '../../../../helpers/Helpers';
import { getToken } from '../../../../api/Account';

import {
	addArqueoSucursalAsync,
	getDataArqueoSucursalAsync,
	getDenominationsAsync,
	getRecuperacionBySucursalAsync
} from '../../../../api/ArqueoCaja';

import { getSucursalByUserAsync, getusersBySucursalAsync } from '../../../../api/Users';
import { getTipoCambioHoyAsync } from '../../../../api/TipoCambio';

import moment from 'moment/moment';

/* ================== HELPERS ================== */

const formatter = new Intl.NumberFormat('es-NI', {
	style: 'currency',
	currency: 'NIO'
});

const buildDetalle = (data) =>
	data.map((item) => ({
		id: item.id,
		description: item.description,
		valor: item.valor,
		cantidad: '',
		total: 0
	}));

const AddArqueoSucursal = () => {
	const navigate = useNavigate();
	const token = getToken();
	const ruta = getRuta();

	const { setIsLoading, setIsUnautorized, selectedSucursal, setSelectedSucursal } =
		useContext(DataContext);

	/* ===== STATES ===== */

	const [sucursalList, setSucursalList] = useState([]);
	const [selectedFecha, setSelectedFecha] = useState(new Date());

	const [dataArqueoSucursal, setDataArqueoSucursal] = useState(null);

	// const [recuperacionSucursal, setRecuperacionSucursal] = useState(0);

	const [denominationList, setDenominationList] = useState([]);
	const [detalleArqueo, setDetalleArqueo] = useState([]);
	const [totalBilletaje, setTotalBilletaje] = useState(0);

	const [tcCompra, setTcCompra] = useState(0);
	// const [saldoInicial, setSaldoInicial] = useState('');
	// const [montoGastos, setMontoGastos] = useState('');
	// const [montoDepositos, setMontoDepositos] = useState('');
	const [observaciones, setObservaciones] = useState('');

	const n = (v) => Number(v) || 0;

	const saldoInicialN = n(dataArqueoSucursal?.montoSaldoInicial || 0);
	const gastosN = n(dataArqueoSucursal?.montoGastos || 0);
	const depositosN = n(dataArqueoSucursal?.montoDepositos || 0);

	const recuperacionEfectivoN = n(dataArqueoSucursal?.montoRecuperacionEfectivo || 0);
	const abonosN = n(dataArqueoSucursal?.montoRecuperacionTransferencia || 0);
	const desembolsosN = n(dataArqueoSucursal?.montoDesembolsos || 0);

	// Solo la recuperación en efectivo representa dinero físico en caja;
	// los abonos por transferencia no forman parte de la entrega.
	const entrega = React.useMemo(() => {
		return saldoInicialN + recuperacionEfectivoN - (gastosN + desembolsosN + depositosN);
	}, [saldoInicialN, recuperacionEfectivoN, gastosN, desembolsosN, depositosN]);

	const diferenciaCalculada = React.useMemo(() => {
		return totalBilletaje - entrega;
	}, [totalBilletaje, entrega]);

	/* ===== ERROR HANDLER ===== */
	const handleError = (result) => {
		setIsLoading(false);
		if (result.error === 'Unauthorized') {
			setIsUnautorized(true);
			return true;
		}
		simpleMessage(result.error, 'error');
		return true;
	};

	/* ===== INIT ===== */

	useEffect(() => {
		const init = async () => {
			setIsLoading(true);

			const suc = await getSucursalByUserAsync(token);
			if (!suc.statusResponse && handleError(suc)) return;

			let sucursalId = 0;
			if (selectedSucursal === 0) {
				setSelectedSucursal(suc.data.result[0].id);
				sucursalId = suc.data.result[0].id;
			} else {
				sucursalId = selectedSucursal;
			}
			setSucursalList(suc.data.result);
			setSelectedSucursal(sucursalId);

			await Promise.all([
				loadDenominaciones(),
				loadTipoCambio(),
				onChangeSucursalDate(sucursalId, selectedFecha)
			]);

			setIsLoading(false);
		};

		init();
	}, []);

	/* ===== LOADERS ===== */

	const loadDenominaciones = async () => {
		const res = await getDenominationsAsync(token);
		if (!res.statusResponse && handleError(res)) return;

		setDenominationList(res.data.result);
		setDetalleArqueo(buildDetalle(res.data.result));
	};

	const loadTipoCambio = async () => {
		const res = await getTipoCambioHoyAsync(token);
		if (!res.statusResponse && handleError(res)) return;

		setTcCompra(res.data.compra);
	};

	const onChangeSucursalDate = async (sucursalId, fecha) => {
		setSelectedSucursal(sucursalId);
		setSelectedFecha(fecha);
		if (!sucursalId) return;

		setIsLoading(true);
		const res = await getDataArqueoSucursalAsync(token, {
			sucursalId,
			fecha: moment(fecha).format('YYYY-MM-DD')
		});
		if (!res.statusResponse && handleError(res)) return;

		setDataArqueoSucursal(res.data.result);
		setIsLoading(false);
	};

	const saveArqueo = async () => {
		if (selectedSucursal === 0 || selectedSucursal === '') {
			simpleMessage('Debe seleccionar una sucursal', 'warning');
			return;
		}
		if (!moment(selectedFecha).isValid()) {
			simpleMessage('Debe seleccionar una fecha válida', 'warning');
			return;
		}

		const data = {
			aqueoUserDetails: detalleArqueo.map((d) => ({
				demonimacionId: d.id,
				cantidad: Number(d.cantidad) || 0,
				total: d.total
			})),
			fechaArqueo: moment(selectedFecha).format('YYYY-MM-DD'),
			saldoInicial: Number(dataArqueoSucursal?.montoSaldoInicial) || 0,
			recuperacion: Number(dataArqueoSucursal?.montoRecuperacionEfectivo) || 0,
			recuperacionTransferencia: Number(dataArqueoSucursal?.montoRecuperacionTransferencia) || 0,

			depositos: Number(dataArqueoSucursal?.montoDepositos) || 0,
			montoGastos: Number(dataArqueoSucursal?.montoGastos) || 0,
			montoDesembolso: Number(dataArqueoSucursal?.montoDesembolsos) || 0,

			diferencia: diferenciaCalculada,
			efectivo: totalBilletaje,
			sucursalId: selectedSucursal,
			observaciones: observaciones,
			tipoCambio: tcCompra
		};

		setIsLoading(true);
		const result = await addArqueoSucursalAsync(token, data);
		if (!result.statusResponse && handleError(result)) return;

		setDetalleArqueo(buildDetalle(denominationList));
		setTotalBilletaje(0);

		setIsLoading(false);
	};

	const onEditDetalle = useCallback(
		(id, valor, cantidad) => {
			const qty = Number(cantidad);
			if (!Number.isFinite(qty)) return;

			const result = detalleArqueo.reduce(
				(acc, item) => {
					let subtotal = item.total;

					if (item.id === id) {
						subtotal = valor * qty;
						if (item.id === 2) subtotal *= tcCompra;
						subtotal = Math.round(subtotal * 100) / 100;

						acc.detalle.push({ ...item, cantidad: qty, total: subtotal });
					} else {
						acc.detalle.push(item);
					}

					acc.total += subtotal;
					return acc;
				},
				{ detalle: [], total: 0 }
			);

			result.total = Math.round(result.total * 100) / 100;

			setDetalleArqueo(result.detalle);
			setTotalBilletaje(result.total);
		},
		[detalleArqueo, tcCompra, dataArqueoSucursal]
	);

	return (
		<div>
			<Container maxWidth="xl">
				<Paper sx={{ p: 2 }}>
					{/* HEADER */}
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Stack direction="row" spacing={2} alignItems="center">
							<Button
								onClick={() => {
									navigate(`${ruta}/cashregister`);
								}}
								style={{
									color: '#2979ff',
									borderColor: '#2979ff'
								}}
								variant="outlined"
							>
								<FontAwesomeIcon
									style={{ marginRight: 10, fontSize: 20 }}
									icon={faChevronCircleLeft}
								/>
								Regresar
							</Button>

							<Typography color={'#00a152'} variant="h5" fontWeight={'bold'}>
								{`Arqueo Sucursal`}
							</Typography>
						</Stack>

						<Stack direction="row" spacing={2} alignItems="center">
							{/* ✅ SELECTOR DE SUCURSAL */}
							<FormControl size="small" sx={{ minWidth: 220 }}>
								<InputLabel>Sucursal</InputLabel>
								<Select
									value={selectedSucursal}
									label="Sucursal"
									onChange={(e) =>
										onChangeSucursalDate(e.target.value, selectedFecha)
									}
								>
									{sucursalList.map((s) => (
										<MenuItem key={s.id} value={s.id}>
											{s.description}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Stack>
					</Stack>

					<hr />
					<Grid container spacing={2}>
						{/* ================= LEFT ================= */}
						<Grid item sm={12} md={6}>
							<Paper sx={{ p: 2 }}>
								<Stack spacing={1}>
									<Typography color={'#00a152'} fontWeight={'bold'}>
										Usuario
									</Typography>

									<hr />
									<DesktopDatePicker
										label="Fecha"
										value={selectedFecha}
										onChange={(v) => onChangeSucursalDate(selectedSucursal, v)}
										renderInput={(params) => (
											<TextField {...params} fullWidth size="small" />
										)}
									/>

									<Stack direction="row" justifyContent="space-between" mt={2}>
										<Typography fontWeight="bold">
											Total Saldo Inicial
										</Typography>
										<Typography fontWeight="bold">
											{formatter.format(
												dataArqueoSucursal?.montoSaldoInicial || 0
											)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between" mt={2}>
										<Typography fontWeight="bold">
											Recuperación en Efectivo
										</Typography>
										<Typography fontWeight="bold">
											{formatter.format(recuperacionEfectivoN)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Recuperación en Depósitos</Typography>
										<Typography fontWeight="bold">
											{formatter.format(abonosN)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Total Ingresos</Typography>
										<Typography fontWeight="bold" color="#00a152">
											{formatter.format(
												dataArqueoSucursal?.montoTotalIngresos || 0
											)}
										</Typography>
									</Stack>

									<hr />

									<Stack direction="row" justifyContent="space-between" mt={2}>
										<Typography fontWeight="bold">Total Depositos</Typography>
										<Typography fontWeight="bold">
											{formatter.format(
												dataArqueoSucursal?.montoDepositos || 0
											)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between" mt={2}>
										<Typography fontWeight="bold">Total Gastos</Typography>
										<Typography fontWeight="bold">
											{formatter.format(dataArqueoSucursal?.montoGastos || 0)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between" mt={2}>
										<Typography fontWeight="bold">Total Desembolsos</Typography>
										<Typography fontWeight="bold">
											{formatter.format(
												dataArqueoSucursal?.montoDesembolsos || 0
											)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Total Egresos</Typography>
										<Typography fontWeight="bold" color="#f50057">
											{formatter.format(
												dataArqueoSucursal?.montoTotalEgresos || 0
											)}
										</Typography>
									</Stack>

									<hr />

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Entrega</Typography>
										<Typography fontWeight="bold" color="#2979ff">
											{formatter.format(entrega)}
										</Typography>
									</Stack>

									<hr />

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Diferencia</Typography>
										<Typography
											fontWeight="bold"
											color={
												diferenciaCalculada === 0 ? '#00a152' : '#ffc400'
											}
										>
											{formatter.format(diferenciaCalculada)}
										</Typography>
									</Stack>

									<TextField
										fullWidth
										size="small"
										label="Observaciones"
										value={observaciones}
										onChange={(e) =>
											setObservaciones(e.target.value.toLocaleUpperCase())
										}
										multiline
									/>
								</Stack>
							</Paper>
						</Grid>

						{/* ================= RIGHT ================= */}
						<Grid item sm={12} md={6}>
							<Paper sx={{ p: 2 }}>
								<Stack spacing={1}>
									<Stack direction="row" justifyContent="space-between" mb={0}>
										<Typography fontWeight="bold">Billetaje</Typography>
										<Typography variant="body2" color="#ffc400">
											Compra: {formatter.format(tcCompra)}
										</Typography>
									</Stack>

									<hr />

									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell sx={{ py: 0.25 }}>
														Denominacion
													</TableCell>
													<TableCell align="center" sx={{ py: 0.25 }}>
														Billetaje
													</TableCell>
													<TableCell align="center" sx={{ py: 0.25 }}>
														C$
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{detalleArqueo.map((row) => {
													const {
														id,
														description,
														valor,
														cantidad,
														total
													} = row;
													return (
														<TableRow
															key={id}
															sx={{
																'& td': {
																	py: 0.25,
																	px: 1
																}
															}}
														>
															<TableCell>{description}</TableCell>
															<TableCell align="center" width={150}>
																<TextField
																	size="small"
																	value={row.cantidad}
																	onChange={(e) =>
																		onEditDetalle(
																			row.id,
																			row.valor,
																			e.target.value
																		)
																	}
																	inputProps={{
																		style: {
																			padding: '2px 6px',
																			height: 26,
																			textAlign: 'center'
																		}
																	}}
																/>
															</TableCell>
															<TableCell align="center">
																<Typography>{total}</Typography>
															</TableCell>
														</TableRow>
													);
												})}

												<TableRow
													sx={{
														'& td': {
															py: 0.25,
															px: 1,
															fontWeight: 'bold',
															color: '#00a152'
														}
													}}
												>
													<TableCell colSpan={2}>
														TOTAL BILLETAJE
													</TableCell>
													<TableCell align="center">
														{formatter.format(totalBilletaje)}
													</TableCell>
												</TableRow>
											</TableBody>
										</Table>
									</TableContainer>
								</Stack>
							</Paper>
						</Grid>

						<Grid item xs={12}>
							<Button
								onClick={() => {
									saveArqueo();
								}}
								style={{
									color: '#2979ff',
									borderColor: '#2979ff',

									borderRadius: 30
								}}
								variant="outlined"
								fullWidth
							>
								<FontAwesomeIcon
									style={{ marginRight: 10, fontSize: 20 }}
									icon={faSave}
								/>
								Guardar Arqueo
							</Button>
						</Grid>
					</Grid>
				</Paper>
			</Container>
		</div>
	);
};

export default AddArqueoSucursal;
